import * as React from 'react';
import AnimateHeight from 'react-animate-height';
import { Transition, TransitionGroup } from 'react-transition-group';
import { AnimationDuration } from '../../utils/Animation';
import { PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { ToastBaseProps, ToastBaseWidget } from './ToastBase';

type TransitionHeight = 'auto' | 0;

export interface ToastAnimatorState {
  transitionHeights: { [key: string]: TransitionHeight };
}

/**
 * This Wrapper will handle animating Toasts in/out.
 * It transitions both their height and opacity.
 * It's built using a combination of React's `TransitionGroup` and a 3rd party library called `react-animate-height`.
 */
export class ToastAnimator extends React.Component<{ style?: React.CSSProperties }, ToastAnimatorState> {
  public state: ToastAnimatorState = {
    transitionHeights: {},
  };

  private handleEnter(toastId: string): void {
    this.setState(({ transitionHeights }) => ({ transitionHeights: { ...transitionHeights, [toastId]: 'auto' } }));
  }

  private handleExit(toastId: string): void {
    this.setState(({ transitionHeights }) => ({ transitionHeights: { ...transitionHeights, [toastId]: 0 } }));
  }

  private getHeight(toastId: string): TransitionHeight {
    return this.state.transitionHeights[toastId] || 0;
  }

  private findToastId(child: React.ReactElement): string {
    const findToastBaseChild = (element: React.ReactElement): React.ReactElement<PropsAndElementAttrs<ToastBaseProps>> | null => {
      // Reach inside the Transition, then the AnimateHeight, (and potentially the AutoDismissWrapper) to get the Toast, and its toastId.
      if (element.type === ToastBaseWidget) {
        return element;
      }

      return element.props?.children ? findToastBaseChild(element.props.children as React.ReactElement) : null;
    };

    const toast = findToastBaseChild(child);

    return toast && toast.props.id || '';
  }

  private childFactory = (child: React.ReactElement): React.ReactElement => {
    const toastId = this.findToastId(child);

    return React.cloneElement(child, {
      height: this.getHeight(toastId),
    });
  }

  public render(): JSX.Element {
    return <TransitionGroup
      // TransitionGroup will not update exiting children's props without a childFactory
      childFactory={this.childFactory}
      style={this.props.style}
    >
      {React.Children.map(this.props.children, (child: React.ReactElement<PropsAndElementAttrs<ToastBaseProps>>, index: number) => {
        const toastId = this.findToastId(child) || index.toString();

        return <Transition
          appear
          key={toastId}
          onEnter={() => this.handleEnter(toastId)}
          onExit={() => this.handleExit(toastId)}
          timeout={AnimationDuration}
        >
          <AnimateHeight
            animateOpacity={true}
            duration={AnimationDuration}
            height={this.getHeight(toastId)}
          >
            {child}
          </AnimateHeight>
        </Transition>;
      })}
    </TransitionGroup>;
  }
}
